import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  #renderingNextPage = false;

  #renderingPreviousPage = false;

  #cacheGeneration = 0;

  /** @type {Map<string, Promise<void>>} */
  #inflightPages = new Map();

  /** @type {Set<number>} */
  #idleCallbackIds = new Set();

  /** @type {MutationObserver | undefined} */
  #filterObserver;

  /** @type {number | undefined} */
  #filterObserverTimeout;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.#cacheGeneration += 1;
    this.#cancelIdleCallbacks();
    this.#clearFilterObserver();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Release detached sentinels before observing the current render's controls.
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;

    if (!viewMorePrevious && !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const { viewMorePrevious: currentPrevious, viewMoreNext: currentNext } = this.refs;
          if (entry.target === currentPrevious) this.#renderPreviousPage();
          else if (entry.target === currentNext) this.#renderNextPage();
        }
      },
      { rootMargin: '100px' }
    );

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url, this.#cacheGeneration);
    } catch (error) {
      console.error(`Failed to fetch ${type} pagination page`, error);
    } finally {
      // Always release a waiting renderer, including on network or parser failure.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {number} [generation] - Cache generation active when the request started
   */
  async #fetchSpecificPage(pageNumber, url = undefined, generation = this.#cacheGeneration) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo) || !this.isConnected) return;

    const key = `${generation}:${pageNumber}`;
    let request = this.#inflightPages.get(key);
    if (!request) {
      request = (async () => {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
        if (!this.isConnected || generation !== this.#cacheGeneration) return;
        this.pages.set(pageNumber, pageContent);
      })();
      this.#inflightPages.set(key, request);
      const clearRequest = () => {
        if (this.#inflightPages.get(key) === request) this.#inflightPages.delete(key);
      };
      request.then(clearRequest, clearRequest);
    }
    await request;
  }

  async #renderNextPage() {
    if (this.#renderingNextPage) return;
    this.#renderingNextPage = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await this.#renderNextPageUnlocked();
    } finally {
      this.#renderingNextPage = false;
      this.removeAttribute('aria-busy');
    }
  }

  async #renderNextPageUnlocked() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    this.#scheduleIdleFetch('next');
  }

  async #renderPreviousPage() {
    if (this.#renderingPreviousPage) return;
    this.#renderingPreviousPage = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await this.#renderPreviousPageUnlocked();
    } finally {
      this.#renderingPreviousPage = false;
      this.removeAttribute('aria-busy');
    }
  }

  async #renderPreviousPageUnlocked() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    this.#scheduleIdleFetch('previous');
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#cacheGeneration += 1;
    this.pages.clear();
    this.#cancelIdleCallbacks();
    this.#clearFilterObserver();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage = this.refs.grid?.dataset.lastPage;
    this.#filterObserver = new MutationObserver((mutations) => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const gridUpdated = mutations.some(
        (mutation) => mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length)
      );

      if (newLastPage !== currentLastPage || gridUpdated) {
        this.#clearFilterObserver();
        if (this.isConnected) this.#observeViewMore();
      }
    });

    const { grid } = this.refs;
    if (grid) {
      this.#filterObserver.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });
      this.#filterObserverTimeout = window.setTimeout(() => this.#clearFilterObserver(), 3000);
    }
  };

  /** @param {'previous' | 'next'} type */
  #scheduleIdleFetch(type) {
    const generation = this.#cacheGeneration;
    const schedule = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 1));
    const id = schedule(() => {
      this.#idleCallbackIds.delete(id);
      if (!this.isConnected || generation !== this.#cacheGeneration) return;
      this.#fetchPage(type);
    });
    this.#idleCallbackIds.add(id);
  }

  #cancelIdleCallbacks() {
    for (const id of this.#idleCallbackIds) {
      window.cancelIdleCallback?.(id);
      window.clearTimeout(id);
    }
    this.#idleCallbackIds.clear();
  }

  #clearFilterObserver() {
    this.#filterObserver?.disconnect();
    this.#filterObserver = undefined;
    if (this.#filterObserverTimeout !== undefined) {
      window.clearTimeout(this.#filterObserverTimeout);
      this.#filterObserverTimeout = undefined;
    }
  }

  #handlePopState = async () => {
    this.#cacheGeneration += 1;
    this.pages.clear();
    this.#cancelIdleCallbacks();
    this.setAttribute('aria-busy', 'true');
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        url: new URL(window.location.href),
      });
    } catch (error) {
      console.error('Failed to restore collection history state', error);
    } finally {
      if (this.isConnected) this.removeAttribute('aria-busy');
    }
  };
}
